import { getFiles } from '../../../api/generic-tables/get-files';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    GenericTableConfigCreationDto,
    GenericTableConfigDto,
    GenericTableConfigPatchDto,
    GenericTableDataFileDto,
    GenericTableResponseConfigDto,
} from '../../../api/generic-tables/dto';
import useAuth from '../../../../hooks/useAuth';
import { isUserInAllowedGroup } from '../../../../utils/route-guard/roles-check';
import { INSIDE_FIFA_TEAM_ADMIN } from '../../../setup/auth';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';

import { getConfigs } from '../../../api/generic-tables/get-configs';
import { GenericTableConfigViewEntry } from './dto';
import { useIntl } from 'react-intl';
import { createTimestampColumn } from '../../../../components/columns/timestamp-column';
import { DataSourceFileColumnCell } from './partials/columns/data-source-file-column';
import { SearchableConfigsTableColumnConfig } from './consts';
import { Column, Row } from 'react-table';
import { ActionsColumn } from './partials/columns/actions-column';
import { updateConfig as requestUpdateConfigApi } from 'inside-fifa/api/generic-tables/update-config';
import { getConfig } from 'inside-fifa/api/generic-tables/get-config';
import { createConfig } from 'inside-fifa/api/generic-tables/create-config';
import { deleteConfiguration } from 'inside-fifa/api/generic-tables/delete-config';

type AvailableDataSourcesState = {
    dataSources: GenericTableDataFileDto[] | undefined;
    refresh(): Promise<void>;
};

const MAX_PAGE_SIZE = 50;

export const useDataSources = (): AvailableDataSourcesState => {
    const abortRefreshRef = useRef(false);
    const [dataSources, setDataSources] = useState<GenericTableDataFileDto[] | undefined>();

    const refresh = useCallback(async () => {
        setDataSources(undefined);

        const firstPage = await getFiles({
            pageIndex: 0,
            pageSize: MAX_PAGE_SIZE,
        });
        setDataSources(firstPage.elements);

        let pageIndex = 1;
        let totalPages = Math.ceil(firstPage.total / MAX_PAGE_SIZE);

        while (pageIndex < totalPages && !abortRefreshRef.current) {
            const data = await getFiles({
                pageIndex,
                pageSize: MAX_PAGE_SIZE,
            });
            setDataSources((prevState) => (prevState ?? []).concat(data.elements));

            pageIndex++;
        }
    }, []);

    useEffect(() => {
        void refresh();

        return () => {
            abortRefreshRef.current = true;
        };
    }, [refresh]);

    return {
        dataSources,
        refresh,
    };
};

type ConfigManagement = {
    configs: GenericTableConfigViewEntry[];
    isManagementAllowed: boolean;
    pageCount: number;
    fetchConfigs: BaseTableFetchFuncProps;
    updateConfig: (id: number, config: GenericTableConfigPatchDto) => Promise<void>;
    duplicateConfig: (configId: number) => Promise<GenericTableConfigDto | null>;
    deleteConfig: (configId: number) => Promise<GenericTableResponseConfigDto>;
    refreshConfigs: () => void;
};

export const useConfigManagement = (): ConfigManagement => {
    const [configDtos, setConfigDtos] = useState([] as GenericTableConfigDto[]);
    const [configs, setConfigs] = useState([] as GenericTableConfigViewEntry[]);
    const [pageCount, setPageCount] = useState(0);
    const [currentPageParams, setCurrentPageParams] = useState<Parameters<BaseTableFetchFuncProps>[0] | null>(null);

    const { user } = useAuth();
    const isManagementAllowed = isUserInAllowedGroup(user?.groups ?? [], [INSIDE_FIFA_TEAM_ADMIN]) || true;

    const { dataSources } = useDataSources();

    useEffect(() => {
        if (!dataSources) {
            return;
        }

        setConfigs(
            configDtos.map((dto) => ({
                ...dto,
                dataSourceFileName: dataSources.find((dataSource) => dataSource.fileId === dto.dataSource.fileId)?.name,
            })),
        );
    }, [dataSources, configDtos]);

    const fetchConfigs: BaseTableFetchFuncProps = useCallback(async (pageParams) => {
        const { pageIndex, pageSize, filters, searchTerm } = pageParams;
        try {
            const response = await getConfigs({
                pageIndex,
                pageSize,
                andSpecifications: filters ? mapFiltersToSpecification(filters) : undefined,
                orSpecifications:
                    searchTerm && searchTerm.length > 0
                        ? mapSearchParamToSpecification(searchTerm, SearchableConfigsTableColumnConfig)
                        : undefined,
            });
            const sortedConfigs = response.elements.sort((a, b) => a.id - b.id);
            setConfigDtos(sortedConfigs);
            setPageCount(Math.ceil(response.total / pageSize));
            setCurrentPageParams(pageParams);
        } catch {
            setConfigDtos([]);
            setPageCount(0);
        }
    }, []);

    const refreshConfigs = useCallback(() => {
        if (!currentPageParams) {
            return;
        }

        fetchConfigs(currentPageParams);
    }, [currentPageParams, fetchConfigs]);

    const updateConfig = useCallback<ConfigManagement['updateConfig']>(
        async (id, configData) => {
            const newConfig = await requestUpdateConfigApi(id, configData);
            const outdatedConfig = configDtos.find((config) => config.id === id);

            if (!outdatedConfig) {
                return;
            }

            // Backend has some bug that does not send back proper data source,
            // Hence quick workaround for this on frontend
            if (newConfig.dataSource.fileId === null) {
                refreshConfigs();
                return;
            }

            setConfigDtos((configs) => configs.map((config) => (config.id === id ? newConfig : config)));
        },
        [refreshConfigs, configDtos],
    );

    const duplicateConfig = useCallback(async (configId: number): Promise<GenericTableConfigDto | null> => {
        try {
            const currentConfig = await getConfig(configId);

            if (!currentConfig) {
                throw new Error(`Cannot find Configuration with id ${configId}`);
            }

            const newConfigData: GenericTableConfigCreationDto = {
                name: `Clone_${currentConfig.name}`,
                dataSource: currentConfig.dataSource,
            };
            const newConfig = await createConfig(newConfigData);

            if (!newConfig) {
                throw new Error('Failed to create new configuration');
            }

            const updatedConfigData: GenericTableConfigPatchDto = {
                ...currentConfig,
                name: newConfig.name,
                modifiedTimestamp: newConfig.modifiedTimestamp,
            };
            const updatedConfig = await requestUpdateConfigApi(newConfig.id, updatedConfigData);

            return updatedConfig;
        } catch (error) {
            return null;
        }
    }, []);

    const deleteConfig = useCallback(
        async (configId: number): Promise<GenericTableResponseConfigDto> => {
            try {
                const currentConfig = await deleteConfiguration(configId);
                if (currentConfig.status) {
                    refreshConfigs();
                    return {
                        status: 200,
                    };
                }
                return currentConfig;
            } catch (error: any) {
                if (error) {
                    const errorStatus = error.status;
                    const errorMessage = error.data?.message || 'An unexpected error occurred.';
                    const errorData = error.data?.data || '';
                    return {
                        status: errorStatus,
                        message: errorMessage,
                        data: errorData,
                    };
                } else {
                    return {
                        status: 500,
                        message: 'Unexpected error',
                    };
                }
            }
        },
        [refreshConfigs],
    );

    return {
        configs,
        fetchConfigs,
        isManagementAllowed,
        pageCount,
        updateConfig,
        duplicateConfig,
        deleteConfig,
        refreshConfigs,
    };
};

type UseConfigTableColumnsConfigDeps = {
    onEdit: (column: GenericTableConfigDto) => void;
    onDuplicate: (column: GenericTableConfigDto) => void;
    onDelete: (column: GenericTableConfigDto) => void;
};

export const useConfigTableColumnsConfig = ({ onEdit, onDuplicate, onDelete }: UseConfigTableColumnsConfigDeps) => {
    const intl = useIntl();
    const { user } = useAuth();

    return useMemo(() => {
        const baseColumns = [
            {
                Header: intl.formatMessage({ id: 'inside-fifa.id' }),
                accessor: 'id',
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.table-name' }),
                accessor: 'name',
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.data-source-file' }),
                accessor: 'dataSourceFileName',
                Cell: DataSourceFileColumnCell,
            },
            {
                Header: intl.formatMessage({ id: 'inside-fifa.generic-tables.upload-date' }),
                accessor: 'createdTimestamp',
                Cell: createTimestampColumn('createdTimestamp'),
            },
        ] as Column<GenericTableConfigViewEntry>[];

        if (isUserInAllowedGroup(user?.groups ?? [], [INSIDE_FIFA_TEAM_ADMIN])) {
            baseColumns.push({
                Header: intl.formatMessage({ id: 'inside-fifa.actions-header' }),
                disableFilters: true,
                Cell({ row }: { row: Row<GenericTableConfigViewEntry> }) {
                    return (
                        <ActionsColumn
                            onEdit={() => onEdit(row.original)}
                            onDuplicate={() => onDuplicate(row.original)}
                            onDelete={() => onDelete(row.original)}
                        />
                    );
                },
            });
        }

        return baseColumns;
    }, [intl, user?.groups, onEdit, onDuplicate, onDelete]);
};
